import "./Profile.css";
import { useParams } from "react-router";
import Header from "../../Components/Shared/Header/Header";
import Footer from "../../Components/Shared/Footer/Footer";
import { useEffect, useState } from "react";
import VivahApi from "../../api";
import { calculateAgeInYearsAndMonths } from "../../utils/common";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { getMasterDataItem, isPlanPurchased } from "../../utils/api-utils";
import { useNavigate } from "react-router-dom";
import authService from "../../services/auth.service";

/* ── helpers ──────────────────────────────────────────────────────────── */

function DetailItem({ label, value }) {
  const empty = !value || value === "Not Specified";
  return (
    <div className="pd-detail-item">
      <div className="pd-detail-label">{label}</div>
      <div className={`pd-detail-value${empty ? " not-specified" : ""}`}>
        {empty ? "Not Specified" : value}
      </div>
    </div>
  );
}

function InfoCard({ icon, title, children }) {
  return (
    <div className="pd-card">
      <div className="pd-card-header">
        <div className="pd-card-header-icon">
          <i className={icon}></i>
        </div>
        <h4 className="pd-card-title">{title}</h4>
      </div>
      <div className="pd-card-body">{children}</div>
    </div>
  );
}

/* ── component ──────────────────────────────────────────────────────────── */
function Profile(props) {
  const navigate = useNavigate();
  const { id } = useParams();

  const [profile, setProfile] = useState({});
  const [master, setMaster] = useState({});
  const [isFav, setIsFav] = useState(false);
  const [isReqSent, setIsReqSent] = useState(false);
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const [profileLoading, setProfileLoading] = useState(true);

  const loadUserData = async () => {
    const [profileRes, masterRes, meRes] = await Promise.all([
      VivahApi.profile.getProfilebyId(id),
      VivahApi.profile.listProfile(),
      VivahApi.Auth.userMe().catch(() => null),
    ]);
    // axios wraps in .data; backend returns {profile, isFvrt, requestSent}
    const body = profileRes.data;
    const p = body.profile ?? {};
    setProfile(p);
    setIsFav(body.isFvrt ?? false);
    setIsReqSent(body.requestSent ?? false);
    // listProfile returns { data: { religions, castes, heights, … } }
    setMaster(masterRes.data ?? {});

    // Phone state from server: phone field is only present when already unlocked
    if (p.phone) {
      setPhoneNumber(p.phone);
      setPhoneVisible(true);
    }
    // Phone view quota from /users/me (server-computed, enforcement is on unlock endpoint)
    if (meRes) {
      const me = meRes.data?.user || meRes.data;
      if (me) {
        setPhoneViewLimit(me.phoneViewLimit ?? 0);
        setViewsRemaining(me.phoneViewsRemaining ?? 0);
      }
    }
    setProfileLoading(false);
  };

  useEffect(() => {
    loadUserData();
  }, [id]);

  // ── helpers to resolve IDs from master data ──────────────────────────
  const resolve = (arr, id) => getMasterDataItem(arr, id)?.name ?? null;

  const handleInterest = async () => {
    await VivahApi.Interest.addInterest(profile.userId);
    loadUserData();
  };

  const handleUserChat = (userData) => {
    if (!userData) return;
    const chatPayload = { ...userData, userId: userData.userId || id };
    localStorage.setItem("chatUser", JSON.stringify(chatPayload));
    window.dispatchEvent(new Event("chatUserChanged"));
  };

  const submitInterest = () => {
    confirmAlert({
      title: "Send Interest",
      message: "Are you sure you want to send interest?",
      buttons: [
        { label: "Yes", onClick: handleInterest },
        { label: "No" },
      ],
    });
  };

  const handleFavClick = async () => {
    await VivahApi.Favourite.addFavourite(profile.userId || profile.sid);
    loadUserData();
  };

  const [phoneVisible, setPhoneVisible] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [viewsRemaining, setViewsRemaining] = useState(null);
  const [phoneViewLimit, setPhoneViewLimit] = useState(0);
  const [phoneError, setPhoneError] = useState(null);

  const handleViewPhone = async () => {
    setPhoneError(null);
    try {
      const profileId = profile.sid || profile.userId;
      const res = await VivahApi.profile.unlockPhone(profileId);
      const data = res.data;
      setPhoneNumber(data.phone);
      setPhoneVisible(true);
      setViewsRemaining(data.remaining);
    } catch (err) {
      setPhoneError(err.response?.data?.error || 'Failed to view phone number. Please try again.');
    }
  };

  // ── derived display values ────────────────────────────────────────────
  const fullName = [profile.firstName, profile.lastName]
    .filter(Boolean)
    .join(" ") || profile.name || "—";

  // dateOfBirth may be a string "YYYY-MM-DD", a timestamp number, or a DynamoDB map
  const dobRaw = profile.dateOfBirth;
  const dobDate = dobRaw
    ? (typeof dobRaw === "number" ? new Date(dobRaw) : new Date(String(dobRaw)))
    : null;
  const age = dobDate && !isNaN(dobDate)
    ? calculateAgeInYearsAndMonths(dobDate)
    : (profile.age ? `${profile.age} yrs` : null);

  const cityName   = resolve(master.cities,  profile.cityId);
  const stateName  = resolve(master.states,  profile.stateId);
  const location   = [cityName, stateName].filter(Boolean).join(", ");

  const heightName        = resolve(master.heights,        profile.heightId);
  const religionName      = resolve(master.religions,      profile.religionId);
  const casteName         = resolve(master.castes,         profile.casteId);
  const gothraName        = resolve(master.gothra,         profile.gothraId);
  const motherTongueName  = resolve(master.motherTongues,  profile.motherTongueId);
  const maritalStatusName = resolve(master.maritalStatuses,profile.maritalStatusId);
  const dietName          = resolve(master.diets,          profile.dietId);
  const bloodGroupName    = resolve(master.bloodGroups,    profile.bloodGroupId);
  const bodyTypeName      = resolve(master.bodyTypes,      profile.bodyTypeId);
  const qualName          = resolve(master.qualifications, profile.qualificationId);
  const profName          = resolve(master.professions,    profile.professionId);
  const profTypeName      = resolve(master.professionTypes,profile.professionTypeId);
  const incomeName        = resolve(master.incomes,        profile.incomeId);
  const familyCityName    = resolve(master.cities,         profile.familyCityId);
  const familyStateName   = resolve(master.states,         profile.familyStateId);
  const familyValuesName  = resolve(master.familyValues,   profile.familyValuesId);
  const familyStatusName  = resolve(master.familyStatuses, profile.familyStatusId);
  const fatherOccName     = resolve(master.professions,    profile.fatherOccupationId);
  const motherOccName     = resolve(master.professions,    profile.motherOccupationId);

  const phoneAvailable = profile.phoneAvailable ?? !!profile.phone;

  const isPaid = isPlanPurchased();
  const photos = (profile.photos && Array.isArray(profile.photos) && profile.photos.length > 0)
    ? profile.photos
    : (profile.photo ? [{ url: profile.photo }] : [{ url: "/assets/images/avatar-placeholder.svg" }]);
  const photoSrc = photos[activePhotoIdx]?.url || "/assets/images/avatar-placeholder.svg";
  const totalPhotos = photos.length;
  const goPrev = () => setActivePhotoIdx(i => (i - 1 + totalPhotos) % totalPhotos);
  const goNext = () => setActivePhotoIdx(i => (i + 1) % totalPhotos);

  return (
    <>
      <div className="gla_page" id="gla_page">
        <a href="#gla_page" className="gla_top ti ti-angle-up gla_go"></a>
        <Header />

        <section id="gla_content" className="gla_content page-top-offset">
          <div className="pd-page">
            <div className="pd-layout">

              {/* ── LEFT: Sticky photo + actions ── */}
              <div className="pd-photo-col">
                <div className="pd-photo-card">
                  <div className="pd-photo-frame">
                    <img
                      src={photoSrc}
                      alt={fullName}
                      className={(!isPaid && activePhotoIdx > 0) ? 'pd-photo-blurred' : ''}
                    />
                    <div className="pd-photo-overlay">
                      <div className="pd-photo-name">{fullName}</div>
                      {age && <div className="pd-photo-sub">{age}</div>}
                    </div>
                    {!isPaid && activePhotoIdx > 0 && (
                      <div className="pd-photo-lock-overlay" onClick={() => navigate('/update')}>
                        <i className="ti ti-lock pd-lock-icon"></i>
                        <div className="pd-lock-title">Photos Locked</div>
                        <div className="pd-lock-sub">Upgrade to view photos</div>
                      </div>
                    )}
                    {totalPhotos > 1 && (
                      <>
                        <button className="pd-carousel-btn pd-carousel-prev" onClick={goPrev} aria-label="Previous photo">
                          <i className="ti ti-angle-left"></i>
                        </button>
                        <button className="pd-carousel-btn pd-carousel-next" onClick={goNext} aria-label="Next photo">
                          <i className="ti ti-angle-right"></i>
                        </button>
                      </>
                    )}
                  </div>
                  {totalPhotos > 1 && (
                    <div className="pd-photo-dots">
                      {photos.map((_, i) => (
                        <button
                          key={i}
                          className={`pd-photo-dot${i === activePhotoIdx ? " active" : ""}`}
                          onClick={() => setActivePhotoIdx(i)}
                          aria-label={`Photo ${i + 1}`}
                        />
                      ))}
                    </div>
                  )}

                  <div className="pd-photo-actions">
                    <button
                      className="pd-btn pd-btn-interest"
                      onClick={submitInterest}
                      disabled={isReqSent}
                    >
                      <i className="ti ti-heart"></i>
                      {isReqSent ? "Interest Sent" : "Send Interest"}
                    </button>
                    <button
                      className="pd-btn pd-btn-fav"
                      onClick={handleFavClick}
                      disabled={isFav}
                    >
                      <i className={isFav ? "ti ti-star" : "ti ti-star-outline"}></i>
                      {isFav ? "Saved" : "Save"}
                    </button>
                    {isPaid ? (
                      <button
                        className="pd-btn pd-btn-chat"
                        onClick={() => handleUserChat(profile)}
                      >
                        <i className="ti ti-comment"></i> Chat
                      </button>
                    ) : (
                      <button
                        className="pd-btn pd-btn-chat pd-btn-locked"
                        onClick={() => navigate('/update')}
                        title="Upgrade to unlock chat"
                      >
                        <i className="ti ti-lock"></i> Chat
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* ── RIGHT: Info sections ── */}
              <div className="pd-info-col">

                {/* Quick stat chips */}
                <div className="pd-stat-chips">
                  {heightName       && <span className="pd-stat-chip"><i className="ti ti-arrows-vertical"></i> {heightName}</span>}
                  {religionName     && <span className="pd-stat-chip"><i className="ti ti-shine"></i> {religionName}</span>}
                  {casteName        && <span className="pd-stat-chip"><i className="ti ti-tag"></i> {casteName}</span>}
                  {maritalStatusName && <span className="pd-stat-chip"><i className="ti ti-heart"></i> {maritalStatusName}</span>}
                  {motherTongueName && <span className="pd-stat-chip"><i className="ti ti-comment"></i> {motherTongueName}</span>}
                  {location         && <span className="pd-stat-chip"><i className="ti ti-location-pin"></i> {location}</span>}
                  {qualName         && <span className="pd-stat-chip"><i className="ti ti-shield"></i> {qualName}</span>}
                  {profName         && <span className="pd-stat-chip"><i className="ti ti-briefcase"></i> {profName}</span>}
                </div>

                {/* About */}
                <InfoCard icon="ti ti-user" title="About">
                  <p className="pd-about-text">
                    {profile.aboutYourself || (
                      <span style={{ color: "#d1d5db", fontStyle: "italic" }}>
                        No description provided
                      </span>
                    )}
                  </p>
                </InfoCard>

                {/* Contact */}
                <InfoCard icon="ti ti-mobile" title="Contact Details">
                  {profileLoading ? (
                    <span style={{ color: '#aaa', fontSize: 13 }}>Loading…</span>
                  ) : phoneVisible ? (
                    /* ── Already unlocked ── */
                    <div>
                      <div className="pd-contact-number">{phoneNumber}</div>
                      {viewsRemaining !== null && (
                        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                          {viewsRemaining} unlock{viewsRemaining !== 1 ? 's' : ''} remaining in your plan
                        </div>
                      )}
                    </div>
                  ) : phoneError ? (
                    /* ── API error ── */
                    <div className="pd-contact-note" style={{ color: '#c00' }}>
                      <i className="ti ti-close" style={{ marginRight: 6 }}></i>{phoneError}
                    </div>
                  ) : !phoneAvailable ? (
                    /* ── Profile has no phone on file ── */
                    <span style={{ color: '#d1d5db', fontStyle: 'italic' }}>Not Specified</span>
                  ) : !isPlanPurchased() ? (
                    /* ── No active plan ── */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div className="pd-contact-note">
                        <i className="ti ti-lock" style={{ marginRight: 6 }}></i>
                        Subscribe to a plan to view phone numbers
                      </div>
                      <button
                        onClick={() => navigate('/update')}
                        style={{
                          background: '#e91e63', color: '#fff', border: 'none',
                          borderRadius: 5, padding: '7px 18px', cursor: 'pointer',
                          fontWeight: 600, fontSize: 13, alignSelf: 'flex-start',
                        }}
                      >
                        View Plans
                      </button>
                    </div>
                  ) : phoneViewLimit === 0 ? (
                    /* ── Silver plan (no phone access) ── */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div className="pd-contact-note">
                        <i className="ti ti-lock" style={{ marginRight: 6 }}></i>
                        Your current plan does not include phone number access
                      </div>
                      <div style={{ fontSize: 12, color: '#888' }}>
                        Upgrade to Gold (20 views), Diamond (30 views), or Platinum (40 views)
                      </div>
                      <button
                        onClick={() => navigate('/update')}
                        style={{
                          background: '#e91e63', color: '#fff', border: 'none',
                          borderRadius: 5, padding: '7px 18px', cursor: 'pointer',
                          fontWeight: 600, fontSize: 13, alignSelf: 'flex-start',
                        }}
                      >
                        Upgrade Plan
                      </button>
                    </div>
                  ) : viewsRemaining !== null && viewsRemaining <= 0 ? (
                    /* ── Quota exhausted ── */
                    <div className="pd-contact-note">
                      <i className="ti ti-lock" style={{ marginRight: 6 }}></i>
                      You have used all {phoneViewLimit} phone unlocks in your plan
                    </div>
                  ) : (
                    /* ── Can unlock ── */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <button
                        onClick={handleViewPhone}
                        style={{
                          background: '#e91e63', color: '#fff', border: 'none',
                          borderRadius: 5, padding: '8px 20px', cursor: 'pointer',
                          fontWeight: 600, fontSize: 14, alignSelf: 'flex-start',
                        }}
                      >
                        <i className="ti ti-eye" style={{ marginRight: 6 }}></i>
                        Unlock Phone Number
                      </button>
                      {viewsRemaining !== null && (
                        <div style={{ fontSize: 12, color: '#888' }}>
                          {viewsRemaining} of {phoneViewLimit} unlock{phoneViewLimit !== 1 ? 's' : ''} remaining in your plan
                        </div>
                      )}
                    </div>
                  )}
                </InfoCard>

                {/* Basics & Lifestyle */}
                <InfoCard icon="ti ti-clipboard" title="Basics & Lifestyle">
                  <div className="pd-detail-grid">
                    <DetailItem label="Age"            value={age} />
                    <DetailItem label="Date of Birth"  value={dobDate && !isNaN(dobDate) ? dobDate.toLocaleDateString() : null} />
                    <DetailItem label="Height"         value={heightName} />
                    <DetailItem label="Marital Status" value={maritalStatusName} />
                    <DetailItem label="Body Weight"    value={profile.bodyWeight ? `${profile.bodyWeight} kg` : null} />
                    <DetailItem label="Complexion"     value={profile.complexion} />
                    <DetailItem label="Diet"           value={dietName} />
                    <DetailItem label="Blood Group"    value={bloodGroupName} />
                    <DetailItem label="Body Type"      value={bodyTypeName} />
                    <DetailItem label="Mother Tongue"  value={motherTongueName} />
                  </div>
                </InfoCard>

                {/* Religious Background */}
                <InfoCard icon="ti ti-shine" title="Religious Background">
                  <div className="pd-detail-grid">
                    <DetailItem label="Religion"      value={religionName} />
                    <DetailItem label="Caste"         value={casteName} />
                    <DetailItem label="Gothra"        value={gothraName} />
                    <DetailItem label="Mother Tongue" value={motherTongueName} />
                    <DetailItem label="Time of Birth" value={profile.birthTime} />
                    <DetailItem label="Place of Birth" value={profile.birthPlace} />
                  </div>
                </InfoCard>

                {/* Family Details */}
                <InfoCard icon="ti ti-home" title="Family Details">
                  <div className="pd-detail-grid">
                    <DetailItem label="Father's Name"       value={profile.fatherName} />
                    <DetailItem label="Father's Occupation" value={fatherOccName} />
                    <DetailItem label="Mother's Name"       value={profile.motherName} />
                    <DetailItem label="Mother's Occupation" value={motherOccName} />
                    <DetailItem label="No. of Brothers"     value={profile.brother != null ? String(profile.brother) : null} />
                    <DetailItem label="No. of Sisters"      value={profile.sister  != null ? String(profile.sister)  : null} />
                    <DetailItem label="Family Values"       value={familyValuesName} />
                    <DetailItem label="Family Status"       value={familyStatusName} />
                    <DetailItem label="Family Location"     value={[familyCityName, familyStateName].filter(Boolean).join(", ")} />
                    <DetailItem label="Living With Family"  value={profile.withFamily} />
                  </div>
                </InfoCard>

                {/* Education & Career */}
                <InfoCard icon="ti ti-briefcase" title="Education & Career">
                  <div className="pd-detail-grid">
                    <DetailItem label="Highest Qualification" value={qualName} />
                    <DetailItem label="College(s) Attended"   value={profile.collegeName} />
                    <DetailItem label="Working With"          value={profTypeName} />
                    <DetailItem label="Working As"            value={profName} />
                    <DetailItem label="Company Name"          value={profile.companyName} />
                    <DetailItem label="Annual Income"         value={incomeName} />
                  </div>
                </InfoCard>

              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

export default Profile;
